/**
 * The access matrix (T4.6) — users down the side, blueprints across the top, and
 * a role in each cell.
 *
 * What it will not offer, because the API refuses it:
 *
 *   - Administrators get no cells. An admin already reaches every blueprint, and
 *     the API answers a grant to one with 422. They are listed in a sentence
 *     instead, so their absence from the grid is explained rather than puzzling.
 *
 * What it shows rather than hides:
 *
 *   - A grant held by a DEACTIVATED user still exists and works again the moment
 *     they are reactivated. The row is marked and each of its grants is labelled
 *     dormant, so it never reads as access that is working today.
 *
 * Every change is a real request (PUT to grant or change, DELETE to revoke) and
 * the grid re-reads from the API afterwards; nothing is remembered locally. A
 * refused change leaves the cell showing what the server says is true.
 */
import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTerms } from '../../lib/terms';
import { useDocumentTitle } from '../../lib/documentTitle';
import { api } from '../../lib/api';
import {
  adminKeys,
  errorText,
  useAllBlueprints,
  useRevokeGrant,
  useSetGrant,
  useUsers,
  type Grant,
  type GrantRole,
} from '../../lib/admin';

type CellValue = 'none' | GrantRole;

const ROLE_OPTIONS: { value: CellValue; label: string }[] = [
  { value: 'none', label: 'No access' },
  { value: 'viewer', label: 'Viewer' },
  { value: 'editor', label: 'Editor' },
];

export function AccessMatrix() {
  const terms = useTerms();
  const users = useUsers();
  const blueprints = useAllBlueprints();
  const setGrant = useSetGrant();
  const revoke = useRevokeGrant();
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const [problems, setProblems] = useState<string[]>([]);

  useDocumentTitle('Access', terms.plural('blueprint'));

  const columns = useMemo(
    () =>
      [...(blueprints.data ?? [])].sort(
        (a, b) =>
          a.client.name.localeCompare(b.client.name, 'en-GB') || a.name.localeCompare(b.name, 'en-GB'),
      ),
    [blueprints.data],
  );

  const grantQueries = useQueries({
    queries: columns.map((b) => ({
      queryKey: adminKeys.access(b.id),
      queryFn: async ({ signal }: { signal: AbortSignal }) =>
        (await api.get<{ grants: Grant[] }>(`/blueprints/${b.id}/access`, signal)).grants,
    })),
  });

  const grantFor = (blueprintIndex: number, userId: string): Grant | undefined =>
    grantQueries[blueprintIndex]?.data?.find((g) => g.userId === userId);

  const people = [...(users.data ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'en-GB'));
  const admins = people.filter((u) => u.globalRole === 'admin');
  const others = people.filter((u) => u.globalRole !== 'admin');

  const change = async (
    userId: string,
    userName: string,
    blueprintId: string,
    blueprintName: string,
    value: CellValue,
  ) => {
    const key = `${userId}:${blueprintId}`;
    setPending((p) => new Set(p).add(key));
    try {
      if (value === 'none') await revoke.mutateAsync({ blueprintId, userId });
      else await setGrant.mutateAsync({ blueprintId, userId, role: value });
    } catch (err) {
      setProblems((list) => [
        `Could not change ${userName}’s access to ${blueprintName}: ${errorText(err)}`,
        ...list,
      ]);
    } finally {
      setPending((p) => {
        const next = new Set(p);
        next.delete(key);
        return next;
      });
    }
  };

  const loading = users.isPending || blueprints.isPending;
  const failed = users.isError || blueprints.isError;

  return (
    <div className="sheet adm-sheet">
      <div className="masthead">
        <div>
          <h1>Access</h1>
          <p className="meta">
            What each person can open. An editor can change and publish; a viewer sees the latest
            published version only. Changes apply on the person’s next request.
          </p>
        </div>
      </div>

      {problems.length > 0 ? (
        <div className="adm-problems-box" role="alert">
          <ul className="adm-problems">
            {problems.map((p, i) => (
              <li key={`${i}-${p}`}>{p}</li>
            ))}
          </ul>
          <button type="button" className="btn" onClick={() => setProblems([])}>
            Dismiss
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="empty" role="status" aria-live="polite">
          Loading…
        </div>
      ) : failed ? (
        <div className="empty" role="alert">
          Could not load the access grid.
          <br />
          {errorText(users.error ?? blueprints.error)}
        </div>
      ) : columns.length === 0 ? (
        <div className="empty">There are no {terms.plural('blueprint')} to grant access to yet.</div>
      ) : others.length === 0 ? (
        <div className="empty">
          There are no non-administrator users. Add one on the Users screen, then grant access here.
        </div>
      ) : (
        <div className="adm-scroll adm-matrix-scroll">
          <table className="adm-table adm-matrix">
            <thead>
              <tr>
                <th scope="col" className="adm-sticky">
                  User
                </th>
                {columns.map((b) => (
                  <th scope="col" key={b.id} title={`${b.name} (${b.client.name})`}>
                    <span className="adm-col-name">{b.name}</span>
                    <span className="adm-col-sub">
                      {b.client.name}
                      {b.isArchived ? ' · archived' : ''}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {others.map((u) => (
                <tr key={u.id} className={u.isActive ? undefined : 'adm-inactive'}>
                  <th scope="row" className="adm-sticky">
                    <span className="adm-name">{u.name}</span>
                    <span className="adm-sub">{u.email}</span>
                    {!u.isActive ? (
                      <span className="adm-sub adm-tag warn">
                        Deactivated — grants are kept but do nothing
                      </span>
                    ) : null}
                  </th>
                  {columns.map((b, i) => {
                    const q = grantQueries[i];
                    const grant = grantFor(i, u.id);
                    const value: CellValue = grant ? grant.role : 'none';
                    const busy = pending.has(`${u.id}:${b.id}`);
                    return (
                      <td key={b.id}>
                        {q?.isError ? (
                          <span className="adm-field-err">Could not load</span>
                        ) : (
                          <>
                            <select
                              value={value}
                              disabled={!q?.data || busy}
                              aria-label={`${u.name}’s access to ${b.name} (${b.client.name})`}
                              onChange={(e) => void change(u.id, u.name, b.id, b.name, e.target.value as CellValue)}
                            >
                              {ROLE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                            {busy ? (
                              <span className="adm-sub">Saving…</span>
                            ) : grant && !u.isActive ? (
                              <span className="adm-sub">Dormant</span>
                            ) : null}
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {admins.length > 0 ? (
        <p className="adm-lede adm-admins">
          Administrators have access to every {terms.noun('blueprint')} and are not listed in the
          grid: {admins.map((a) => a.name).join(', ')}.
        </p>
      ) : null}
    </div>
  );
}
